import { BeneficiaryType } from "../types/BeneficiaryType";
import { MezBeneficiary, Trust } from "./estateProfile.model";
import { Person, getPersonDisplayName } from "@services/zok";

export interface DropdownStateChangeEvent {
  activeItems?: string[];
}

export type BeneficiarySectionType = "PRIMARY" | "CONTINGENT";

export interface PersonUpdateData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  stateOfResidence: string;
}

export interface ExistingBeneficiary {
  kind: "existing";
  householdMemberId: string;
  type: BeneficiarySectionType;
  distributionPercentage: number;
  beneficiaryType: BeneficiaryType;
}

export interface TrustCreateData {
  legalName: string;
  displayName: string;
  trustType: string;
  settlor: string;
}

export interface NewBeneficiary {
  kind: "new";
  householdMemberId: null;
  personData?: PersonUpdateData;
  trustData?: TrustCreateData;
  type: BeneficiarySectionType;
  distributionPercentage: number;
  beneficiaryType: BeneficiaryType;
}

export type Beneficiary = ExistingBeneficiary | NewBeneficiary;

export interface EditBeneficiaryPayload {
  beneficiary: ExistingBeneficiary;
  personData: PersonUpdateData;
}

export interface EditTrustBeneficiaryPayload {
  beneficiary: ExistingBeneficiary;
  trustData: TrustCreateData;
}

export interface BeneficiaryState {
  primaryBeneficiaries: Beneficiary[];
  backupBeneficiaries: Beneficiary[];
}

export function createEmptyBeneficiaryState(): BeneficiaryState {
  return {
    primaryBeneficiaries: [],
    backupBeneficiaries: [],
  };
}

export function calculateDistributionTotal(beneficiaries: Beneficiary[]): number {
  return beneficiaries.reduce((sum, bene) => sum + (bene.distributionPercentage || 0), 0);
}

/**
 * Restores BeneficiaryState from MEZ GET response beneficiaries.
 * MEZ returns { associatedMemberId, type, distributionPercentage } — we map these
 * to Beneficiary[], splitting into primary and backup arrays.
 *
 * Beneficiary type is derived by cross-referencing associatedMemberId against the
 * trusts array. If the ID matches a trust's householdMemberId, it's a TRUST beneficiary.
 * Otherwise defaults to INDIVIDUAL_BY_NAME.
 *
 * IMPORTANT: Filters out orphaned beneficiaries whose associatedMemberId doesn't exist
 * in either the people or trusts arrays. This prevents blank entries in the UI.
 */
export function restoreBeneficiaryState(
  mezBeneficiaries: MezBeneficiary[],
  trusts: Trust[] = [],
  people: Person[] = [],
): BeneficiaryState {
  const primary: Beneficiary[] = [];
  const backup: Beneficiary[] = [];
  const trustIds = new Set(trusts.map((t) => t.householdMemberId).filter(Boolean));
  const peopleIds = new Set(people.map((p) => p.householdMemberId).filter(Boolean));

  for (const bene of mezBeneficiaries) {
    // Filter out orphaned beneficiaries that don't exist in either people or trusts
    const existsInTrusts = trustIds.has(bene.associatedMemberId);
    const existsInPeople = peopleIds.has(bene.associatedMemberId);

    if (!existsInTrusts && !existsInPeople) {
      console.warn(
        `Skipping orphaned beneficiary with associatedMemberId: ${bene.associatedMemberId}`,
      );
      continue;
    }

    const beneficiaryType = existsInTrusts
      ? BeneficiaryType.TRUST
      : BeneficiaryType.INDIVIDUAL_BY_NAME;

    const selection: ExistingBeneficiary = {
      kind: "existing",
      householdMemberId: bene.associatedMemberId,
      type: bene.type,
      distributionPercentage: bene.distributionPercentage,
      beneficiaryType,
    };

    if (bene.type === "CONTINGENT") {
      backup.push(selection);
    } else {
      primary.push(selection);
    }
  }

  return { primaryBeneficiaries: primary, backupBeneficiaries: backup };
}

// sm breakpoint: ≥576px uses medium density (24px padding), <576px uses small (16px padding)
export function getCardDensity(): "small" | "medium" {
  return window.innerWidth < 576 ? "small" : "medium";
}

export function getTrustLabel(trust: Trust): string {
  return trust.displayName || trust.legalName || "Unnamed Trust";
}

/**
 * Gets display name for a beneficiary.
 * - Trust beneficiaries: looks up Trust from trusts array, returns displayName or legalName.
 * - New individual beneficiaries: uses personData.firstName/lastName.
 * - Existing individual beneficiaries: looks up Person from householdPeople.
 */
export function getBeneficiaryDisplayName(
  beneficiary: Beneficiary | null,
  householdPeople: Person[],
  trusts: Trust[] = [],
): string {
  if (!beneficiary) return "";

  if (beneficiary.beneficiaryType === BeneficiaryType.TRUST) {
    const trust = trusts.find((t) => t.householdMemberId === beneficiary.householdMemberId);
    return trust?.displayName || trust?.legalName || "";
  }

  if (beneficiary.kind === "new") {
    if (beneficiary.personData) {
      return `${beneficiary.personData.firstName} ${beneficiary.personData.lastName}`;
    }
    if (beneficiary.trustData) {
      return beneficiary.trustData.displayName || beneficiary.trustData.legalName;
    }
    return "";
  }

  const person = householdPeople.find((p) => p.householdMemberId === beneficiary.householdMemberId);
  return person ? getPersonDisplayName(person) : "";
}
